import {useCallback, useEffect, useMemo, useState} from 'react';
import {observer} from 'mobx-react-lite';
import {useLocation, useNavigate} from 'react-router';
import {useQueries, useQuery, UseQueryResult} from 'react-query';
import {useStoreContext} from '@store/store-context';
import {Body2} from '@ui/atoms/typography';
import {EmptyPlaceholder} from '@ui/atoms/empty-placeholder';
import {ItemLoader} from '@ui/organisms/item-loader';
import {cloudinary} from '@assets/images/cloudinary';
import {
  ICheckinCycle,
  ICheckinCycleDetail,
  ISpecificCheckinsWithResponse,
  ISpecificCheckinsWithResponseData,
} from '@pages/dashboard/company-legacy/company-components/company-checkins/company-checkins.interface';
import {CompanyCheckinsRequestImpl} from '@pages/dashboard/company-legacy/company-components/company-checkins/company-checkins.request';
import {CompanyCheckinsController} from '@pages/dashboard/company-legacy/company-components/company-checkins/company-checkins.controller';
import {checkinName} from '@utils/checkin-name';
import {capitalize} from '@utils';
import {
  ManagedCycle,
  ManagedSubmission,
  getSubmissionCycleId,
  isSubmissionSubmitted,
  mergeAudienceWithOccurrence,
  resolveCycleAudience,
} from '../team-management/team-management.utils';
import {canAdministerCheckins} from '../checkins-access';
import {CycleSelectorDropdown} from './cycle-selector-dropdown';
import {SendSelectorDropdown} from './components/send-selector-dropdown';
import {SubmissionRecord} from './components/submission-card';
import {TeamFeedResponseCard} from './components/team-feed-response-card';
import {getCreatorName} from './checkin-submit.utils';
import {
  buildTeamFeedOccurrences,
  buildPublicTeamFeedCycles,
  formatSendOptionLabel,
  getSafeTeamFeedSearch,
  getTeamFeedEligibilityFilter,
  getTeamFeedPublicFilter,
  getTeamFeedMentionableUserIds,
  getTeamFeedParticipation,
  getVisiblePublicTeamFeedSubmissions,
} from './team-feed.utils';

type TeamFeedSubmissionRecord = ManagedSubmission &
  Omit<SubmissionRecord, 'cycle' | 'user'> & {
    responses?: {
      pulse?: {feedback?: string | null; mood?: string | null};
    };
  };

const getCheckinResponseData = (
  response?:
    | ISpecificCheckinsWithResponse
    | ISpecificCheckinsWithResponseData
    | false
    | null,
) => {
  if (!response) return null;
  return 'data' in response ? response.data : response;
};

export const MyCheckinsTeamFeed = observer(() => {
  const {
    authStore: {auth},
    usersStore: {users},
  } = useStoreContext();
  const location = useLocation();
  const navigate = useNavigate();
  const [selectedCycleId, setSelectedCycleId] = useState<string | null>(null);
  const [selectedOccurrenceKey, setSelectedOccurrenceKey] = useState('');
  const [cycleSearchValue, setCycleSearchValue] = useState('');
  const request = useMemo(() => new CompanyCheckinsRequestImpl(), []);
  const controller = useMemo(() => new CompanyCheckinsController(request), [
    request,
  ]);
  const urlCycleId = useMemo(
    () => new URLSearchParams(location.search).get('cycleId'),
    [location.search],
  );
  const urlSend = useMemo(
    () => new URLSearchParams(location.search).get('send'),
    [location.search],
  );
  const urlComments = useMemo(
    () => new URLSearchParams(location.search).get('comments'),
    [location.search],
  );
  const workspaceId = auth.user.workspace.id;
  const canFetchTeamCheckins = Boolean(workspaceId);
  const canManageResponses = canAdministerCheckins(auth.user.role);

  const {
    data: participantResponse,
    isLoading: isLoadingParticipants,
  } = useQuery(
    ['team-checkins', workspaceId, auth.user.id, 'public', 'user'],
    () => controller.getListOfCheckins(getTeamFeedPublicFilter('user')),
    {
      enabled: canFetchTeamCheckins,
      refetchOnWindowFocus: false,
    },
  );

  const {data: cycleResponse, isLoading: isLoadingCyclePopulation} = useQuery(
    ['team-checkins', workspaceId, auth.user.id, 'public', 'cycle'],
    () => controller.getListOfCheckins(getTeamFeedPublicFilter('cycle')),
    {
      enabled: canFetchTeamCheckins,
      refetchOnWindowFocus: false,
    },
  );

  const {data: eligibilityResponse, isLoading: isLoadingEligibility} = useQuery(
    ['team-checkins', workspaceId, auth.user.id, 'public', 'eligibility'],
    () =>
      controller.getListOfCheckins(getTeamFeedEligibilityFilter(auth.user.id)),
    {
      enabled: canFetchTeamCheckins && !canManageResponses,
      refetchOnWindowFocus: false,
    },
  );

  const allSubmissions = useMemo<TeamFeedSubmissionRecord[]>(
    () =>
      getVisiblePublicTeamFeedSubmissions(
        participantResponse,
        cycleResponse,
        eligibilityResponse,
        auth.user.id,
        canManageResponses,
        auth.user.workspace?.timezone,
      ) as TeamFeedSubmissionRecord[],
    [
      auth.user.id,
      auth.user.workspace?.timezone,
      canManageResponses,
      cycleResponse,
      eligibilityResponse,
      participantResponse,
    ],
  );
  const cycles = useMemo<ManagedCycle[]>(
    () => buildPublicTeamFeedCycles(allSubmissions),
    [allSubmissions],
  );
  useEffect(() => {
    if (!cycles.length) return;
    const urlCycle = urlCycleId
      ? cycles.find((cycle) => cycle.id === urlCycleId)
      : undefined;
    const currentCycle = selectedCycleId
      ? cycles.find((cycle) => cycle.id === selectedCycleId)
      : undefined;
    const nextCycle =
      urlCycle ||
      currentCycle ||
      cycles.find((cycle) => cycle.status === 'active') ||
      cycles[0];
    if (nextCycle?.id && nextCycle.id !== selectedCycleId) {
      setSelectedCycleId(nextCycle.id);
      setSelectedOccurrenceKey('');
    }
  }, [cycles, selectedCycleId, urlCycleId]);

  useEffect(() => {
    if (
      isLoadingParticipants ||
      isLoadingCyclePopulation ||
      isLoadingEligibility ||
      !urlCycleId
    ) {
      return;
    }
    if (cycles.some((cycle) => cycle.id === urlCycleId)) return;

    navigate(
      {
        pathname: location.pathname,
        search: getSafeTeamFeedSearch(location.search),
      },
      {replace: true},
    );
  }, [
    cycles,
    isLoadingCyclePopulation,
    isLoadingEligibility,
    isLoadingParticipants,
    location.pathname,
    location.search,
    navigate,
    urlCycleId,
  ]);

  const selectedCycle = useMemo(
    () => cycles.find((cycle) => cycle.id === selectedCycleId) || null,
    [cycles, selectedCycleId],
  );
  const cycleDetails = selectedCycle as ICheckinCycleDetail | null;
  const cycleSubmissions = useMemo(
    () =>
      allSubmissions.filter(
        (submission) => getSubmissionCycleId(submission) === selectedCycleId,
      ),
    [allSubmissions, selectedCycleId],
  );
  const occurrences = useMemo(
    () =>
      selectedCycle
        ? buildTeamFeedOccurrences(
            cycleSubmissions,
            (cycleDetails || selectedCycle) as ManagedCycle,
            auth.user.workspace?.timezone,
          )
        : [],
    [
      auth.user.workspace?.timezone,
      cycleDetails,
      cycleSubmissions,
      selectedCycle,
    ],
  );

  useEffect(() => {
    if (!occurrences.length) {
      setSelectedOccurrenceKey('');
      return;
    }
    const requestedOccurrence = urlSend
      ? occurrences.find((item) => item.key === urlSend)
      : undefined;
    if (
      requestedOccurrence &&
      requestedOccurrence.key !== selectedOccurrenceKey
    ) {
      setSelectedOccurrenceKey(requestedOccurrence.key);
      return;
    }
    if (!occurrences.some((item) => item.key === selectedOccurrenceKey)) {
      setSelectedOccurrenceKey(occurrences[0].key);
    }
  }, [occurrences, selectedOccurrenceKey, urlSend]);

  const selectedOccurrence =
    occurrences.find((item) => item.key === selectedOccurrenceKey) ||
    occurrences[0];
  const expectedAudience = useMemo(
    () =>
      selectedCycle ? resolveCycleAudience(selectedCycle, users) : [],
    [selectedCycle, users],
  );
  const participationOccurrence = useMemo(() => {
    if (
      !selectedOccurrence ||
      !selectedCycle ||
      selectedOccurrence.key !== occurrences[0]?.key
    ) {
      return selectedOccurrence;
    }
    return {
      ...selectedOccurrence,
      submissions: mergeAudienceWithOccurrence(
        selectedOccurrence.submissions,
        expectedAudience,
        selectedCycle,
      ),
    };
  }, [expectedAudience, occurrences, selectedCycle, selectedOccurrence]);
  const participation = getTeamFeedParticipation(participationOccurrence);
  const scheduledCount =
    participationOccurrence?.submissions.filter(
      (submission) => submission.availabilityState === 'scheduled',
    ).length || 0;
  const mentionableUserIds = useMemo(
    () =>
      getTeamFeedMentionableUserIds(
        participationOccurrence?.submissions || [],
        auth.user.id,
      ),
    [auth.user.id, participationOccurrence],
  );
  const submittedRows = useMemo(
    () =>
      ((participationOccurrence?.submissions || []) as TeamFeedSubmissionRecord[])
        .filter(
          (submission) => submission.id && isSubmissionSubmitted(submission),
        )
        .sort((left, right) =>
          String(right.submittedAt || right.updatedAt || '').localeCompare(
            String(left.submittedAt || left.updatedAt || ''),
          ),
        ),
    [participationOccurrence],
  );

  const detailQueries = useQueries(
    submittedRows.map((submission) => ({
      queryKey: [
        'team-feed-submission-response',
        workspaceId,
        'public',
        submission.id,
      ],
      queryFn: async () => {
        const response = await controller.getCheckinByIdAndFetchResponses(
          submission.id as string,
        );
        const detail = getCheckinResponseData(response);
        if (!detail) throw new Error('Unable to load response');
        return detail;
      },
      enabled: Boolean(submission.id),
      refetchOnWindowFocus: false,
      retry: false,
    })),
  ) as UseQueryResult<ISpecificCheckinsWithResponseData>[];

  const detailsBySubmissionId = useMemo(
    () =>
      submittedRows.reduce<
        Record<string, UseQueryResult<ISpecificCheckinsWithResponseData>>
      >((map, submission, index) => {
        if (submission.id) map[submission.id] = detailQueries[index];
        return map;
      }, {}),
    [detailQueries, submittedRows],
  );

  const activeCycleName = selectedCycle?.cycleName || capitalize(checkinName());
  const getCycleSenderLabel = useCallback(
    (cycle: ICheckinCycle) => {
      const creatorName = getCreatorName(cycle.createdBy, [
        auth.user,
        ...users,
      ]);
      return creatorName === 'Cycle creator'
        ? 'Sent by cycle creator'
        : `Sent by ${creatorName}`;
    },
    [auth.user, users],
  );
  const isLoading =
    isLoadingParticipants || isLoadingCyclePopulation || isLoadingEligibility;

  if (!canFetchTeamCheckins) return null;

  if (isLoading && !selectedCycle) {
    return (
      <div className="flex justify-center py-8">
        <ItemLoader />
      </div>
    );
  }

  return (
    <div className="flex w-full flex-col gap-6">
      <section className="flex flex-col gap-4 rounded-[10px] border border-borderLight bg-white p-4 sm:p-6">
        <div className="flex flex-col gap-2">
          <Body2 kind="textDark" weight="semibold">
            Cycle
          </Body2>
          <CycleSelectorDropdown
            cycles={cycles}
            selectedCycleName={activeCycleName}
            searchValue={cycleSearchValue}
            onSearchChange={setCycleSearchValue}
            onCycleSelect={(cycleId) => {
              setSelectedCycleId(cycleId);
              setSelectedOccurrenceKey('');
            }}
            getCycleSecondaryLabel={getCycleSenderLabel}
            showStatusDots={false}
            fullWidth
          />
        </div>
        <div className="flex flex-col gap-2">
          <Body2 kind="textDark" weight="semibold">
            Send
          </Body2>
          <SendSelectorDropdown
            occurrences={occurrences}
            selectedOccurrence={selectedOccurrence}
            onSelect={setSelectedOccurrenceKey}
          />
        </div>
      </section>

      <section aria-label="Participation" className="flex flex-col gap-3 px-1">
        <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
          <Body2 kind="textDark" weight="semibold">
            {selectedOccurrence
              ? `Sent ${formatSendOptionLabel(selectedOccurrence, false)}`
              : 'No sends available'}
          </Body2>
          <Body2 kind="textBody" weight="semibold">
            {participation.submittedCount} of {participation.totalCount}{' '}
            submitted · {participation.percentage}%
            {scheduledCount ? ` · ${scheduledCount} scheduled` : ''}
          </Body2>
        </div>
        <div
          role="progressbar"
          aria-valuemin={0}
          aria-valuemax={100}
          aria-valuenow={participation.percentage}
          aria-label={`${participation.percentage}% participation`}
          className="h-2 w-full overflow-hidden rounded-full bg-borderLight">
          <div
            className="h-full rounded-full bg-purple300 transition-[width] duration-300"
            style={{width: `${participation.percentage}%`}}
          />
        </div>
      </section>

      {isLoading ? (
        <div className="flex justify-center py-8">
          <ItemLoader />
        </div>
      ) : participation.submittedCount === 0 ? (
        <section className="rounded-[10px] border border-borderLight bg-white">
          <EmptyPlaceholder
            title="No responses yet"
            subtitle="Responses will appear here as teammates submit."
            image={{
              alt: 'No responses yet',
              shadow: '-10px 44px 28px 0 rgba(19, 17, 17, 0.12)',
              src: cloudinary.emptyGoals,
              style: {height: '140px', width: '130px'},
            }}
          />
        </section>
      ) : (
        <section
          className="flex flex-col gap-4"
          aria-label="Submitted responses">
          {submittedRows.map((submission) => {
            const detailQuery = submission.id
              ? detailsBySubmissionId[submission.id]
              : undefined;
            return (
              <TeamFeedResponseCard
                key={submission.id}
                submission={submission}
                mentionableUserIds={mentionableUserIds}
                detail={detailQuery?.data}
                cycle={cycleDetails}
                isLoadingDetail={detailQuery?.isLoading}
                detailError={detailQuery?.isError}
                canManageResponses={canManageResponses}
                onRetryDetail={() => detailQuery?.refetch()}
                defaultCommentsOpen={submission.id === urlComments}
              />
            );
          })}
        </section>
      )}
    </div>
  );
});

export default MyCheckinsTeamFeed;
